import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { useDateFormatter } from './useDateFormatter';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Odoo_tickets_CreateInput';
import '../contracts/Odoo_tickets_ReplyInput';
import '../contracts/OdooTickets_CreateResponse';
import '../contracts/OdooTickets_GetResponse';
import '../contracts/OdooTickets_ListTicketsResponse';
import '../contracts/OdooTickets_MessagesResponse';
import '../contracts/OdooTickets_ReplyResponse';

import type { Odoo_tickets_CreateInput } from '../contracts/Odoo_tickets_CreateInput';
/**
 * Composable for managing support tickets in Odoo Help Desk
 *
 * @returns {Object} Ticket management methods and state
 */
export function useOdooTicket() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const tickets = ref<any[]>([]);
  const totalTickets = ref(0);
  const currentTicket = ref<any>(null);
  const ticketMessages = ref<any[]>([]);

  const { fromApiDateTimeFormat } = useDateFormatter();

  /**
   * Create a new support ticket
   *
   * @param {Object} ticketData - Ticket data
   * @param {string} ticketData.name - Ticket subject/title
   * @param {string} ticketData.description - HTML description from rich text editor
   * @param {string} ticketData.page_uri - Current page URL
   * @returns {Promise<Object>} Created ticket data or error
   */
  const createTicket = async (ticketData: Odoo_tickets_CreateInput) => {
    log('useOdooTicket', 'createTicket', 'Creating ticket with data:', {
      name: ticketData.name,
      nameLength: ticketData.name?.length,
      description: ticketData.description?.substring(0, 100) + '...',
      descriptionLength: ticketData.description?.length,
      page_uri: ticketData.page_uri
    });

    isLoading.value = true;
    error.value = null;

    try {
      log('useOdooTicket', 'createTicket', 'Sending POST to /odoo_tickets/create');
      validate('Odoo_tickets_CreateInput', ticketData);
      const response = await httpService.post('/odoo_tickets/create', ticketData, {
        responseContract: 'OdooTickets_CreateResponse'
      });

      log('useOdooTicket', 'createTicket', 'Response received', response);

      if (response.success) {
        return {
          success: true,
          ticketId: response.dados?.ticket_id,
          message: response.dados?.message || 'Ticket criado com sucesso'
        };
      } else {
        const errorMessage = response.error || 'Erro ao criar ticket';
        error.value = errorMessage;
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (err) {
      log('useOdooTicket', 'createTicket', 'Error creating ticket', err);
      const errorMessage = err.message || 'Erro de conexão ao criar ticket';
      error.value = errorMessage;
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get the current page URI for ticket context
   *
   * @returns {string} Current page URL
   */
  const getCurrentPageUri = () => {
    return window.location.href;
  };

  /**
   * Transform API ticket data to typed data (Date objects)
   *
   * @param {Object} ticket - Ticket from API
   * @returns {Object} Transformed ticket with Date objects
   */
  const transformTicket = (ticket: any) => {
    return {
      ...ticket,
      created_at: ticket.created_at ? fromApiDateTimeFormat(ticket.created_at) : null,
      updated_at: ticket.updated_at ? fromApiDateTimeFormat(ticket.updated_at) : null
    };
  };

  /**
   * Transform API message data to typed data (Date objects)
   *
   * @param {Object} message - Message from API
   * @returns {Object} Transformed message with Date objects
   */
  const transformMessage = (message: any) => {
    return {
      ...message,
      date: message.date ? fromApiDateTimeFormat(message.date) : null
    };
  };

  /**
   * Fetch list of tickets for the current user
   *
   * @param {Object} options - Pagination options
   * @param {number} options.limit - Max tickets to return
   * @param {number} options.offset - Pagination offset
   * @returns {Promise<Object>} List of tickets
   */
  const fetchTickets = async ({ limit = 50, offset = 0 } = {}) => {
    log('useOdooTicket', 'fetchTickets', 'Fetching tickets', { limit, offset });

    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/odoo_tickets/list_tickets', {
        params: { limit, offset },
        responseContract: 'OdooTickets_ListTicketsResponse'
      });

      log('useOdooTicket', 'fetchTickets', 'Response received', response);

      if (response.success) {
        const rawTickets = response.dados?.tickets || [];
        tickets.value = rawTickets.map(transformTicket);
        totalTickets.value = response.dados?.total ?? rawTickets.length;

        return {
          success: true,
          tickets: tickets.value,
          total: totalTickets.value
        };
      } else {
        const errorMessage = response.error || 'Erro ao listar tickets';
        error.value = errorMessage;
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (err) {
      log('useOdooTicket', 'fetchTickets', 'Error fetching tickets', err);
      const errorMessage = err.message || 'Erro de conexão ao listar tickets';
      error.value = errorMessage;
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch a single ticket by ID
   *
   * @param {number} ticketId - Ticket ID
   * @returns {Promise<Object>} Ticket data
   */
  const fetchTicket = async (ticketId: number) => {
    log('useOdooTicket', 'fetchTicket', 'Fetching ticket', { ticketId });

    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/odoo_tickets/get/${ticketId}`, {
        responseContract: 'OdooTickets_GetResponse'
      });

      log('useOdooTicket', 'fetchTicket', 'Response received', response);

      if (response.success) {
        const rawTicket = response.dados?.ticket || null;
        currentTicket.value = rawTicket ? transformTicket(rawTicket) : null;

        return {
          success: true,
          ticket: currentTicket.value
        };
      } else {
        const errorMessage = response.error || 'Erro ao buscar ticket';
        error.value = errorMessage;
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (err) {
      log('useOdooTicket', 'fetchTicket', 'Error fetching ticket', err);
      const errorMessage = err.message || 'Erro de conexão ao buscar ticket';
      error.value = errorMessage;
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch messages for a ticket
   *
   * @param {number} ticketId - Ticket ID
   * @returns {Promise<Object>} List of messages
   */
  const fetchTicketMessages = async (ticketId: number) => {
    log('useOdooTicket', 'fetchTicketMessages', 'Fetching messages', { ticketId });

    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/odoo_tickets/messages/${ticketId}`, {
        responseContract: 'OdooTickets_MessagesResponse'
      });

      log('useOdooTicket', 'fetchTicketMessages', 'Response received', response);

      if (response.success) {
        const rawMessages = response.dados?.messages || [];
        ticketMessages.value = rawMessages.map(transformMessage);

        return {
          success: true,
          messages: ticketMessages.value
        };
      } else {
        const errorMessage = response.error || 'Erro ao buscar mensagens';
        error.value = errorMessage;
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (err) {
      log('useOdooTicket', 'fetchTicketMessages', 'Error fetching messages', err);
      const errorMessage = err.message || 'Erro de conexão ao buscar mensagens';
      error.value = errorMessage;
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Send a reply to a ticket
   *
   * @param {number} ticketId - Ticket ID
   * @param {string} message - HTML message content
   * @returns {Promise<Object>} Reply result
   */
  const replyToTicket = async (ticketId: number, message: string) => {
    log('useOdooTicket', 'replyToTicket', 'Sending reply', {
      ticketId,
      messageLength: message?.length
    });

    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post('/odoo_tickets/reply', {
        ticket_id: ticketId,
        message
      }, {
        responseContract: 'OdooTickets_ReplyResponse',
        inputContract: 'Odoo_tickets_ReplyInput'
      });

      log('useOdooTicket', 'replyToTicket', 'Response received', response);

      if (response.success) {
        return {
          success: true,
          messageId: response.dados?.message_id,
          message: response.dados?.message || 'Resposta enviada com sucesso'
        };
      } else {
        const errorMessage = response.error || 'Erro ao enviar resposta';
        error.value = errorMessage;
        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (err) {
      log('useOdooTicket', 'replyToTicket', 'Error sending reply', err);
      const errorMessage = err.message || 'Erro de conexão ao enviar resposta';
      error.value = errorMessage;
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Clear current ticket and messages state
   */
  const clearCurrentTicket = () => {
    currentTicket.value = null;
    ticketMessages.value = [];
  };

  return {
    // State
    isLoading,
    error,
    tickets,
    totalTickets,
    currentTicket,
    ticketMessages,

    // Methods
    createTicket,
    getCurrentPageUri,
    fetchTickets,
    fetchTicket,
    fetchTicketMessages,
    replyToTicket,
    clearCurrentTicket
  };
}
